export type DateInput = string | number | Date | null | undefined

function toDate(value: DateInput): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  if (typeof value === 'string') {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch
      const date = new Date(Number(year), Number(month) - 1, Number(day))
      return Number.isNaN(date.getTime()) ? null : date
    }
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function toUtcDate(value: DateInput): Date | null {
  if (value == null || value === '') return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** Format date as dd/MM/yyyy */
export function formatDateEu(value: DateInput, fallback = ''): string {
  const date = toDate(value)
  if (!date) return fallback
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

/** Compact date label for chart axes: dd/MM */
export function formatDateShortEu(value: DateInput, fallback = ''): string {
  const date = toDate(value)
  if (!date) return fallback
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  return `${day}/${month}`
}

/** Format month label as MM/yyyy */
export function formatMonthYearEu(value: DateInput, fallback = ''): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
    const [year, month] = value.split('-')
    return `${month}/${year}`
  }
  const date = toDate(value)
  if (!date) return fallback
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${month}/${year}`
}

/** Format date-time as dd/MM/yyyy HH:mm */
export function formatDateTimeEu(value: DateInput, fallback = ''): string {
  const date = toDate(value)
  if (!date) return fallback
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${formatDateEu(date)} ${hours}:${minutes}`
}

/** Format date-time with seconds as dd/MM/yyyy HH:mm:ss */
export function formatDateTimeFullEu(value: DateInput, fallback = ''): string {
  const date = toDate(value)
  if (!date) return fallback
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${formatDateTimeEu(date)}:${seconds}`
}

/** Format UTC date as dd/MM/yyyy */
export function formatDateEuUtc(value: DateInput, fallback = ''): string {
  const date = toUtcDate(value)
  if (!date) return fallback
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

/** Compact UTC date label for chart axes: dd/MM */
export function formatDateShortEuUtc(value: DateInput, fallback = ''): string {
  const date = toUtcDate(value)
  if (!date) return fallback
  const day = String(date.getUTCDate()).padStart(2, '0')
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  return `${day}/${month}`
}

/** Format UTC month label as MM/yyyy */
export function formatMonthYearEuUtc(value: DateInput, fallback = ''): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}$/.test(value)) {
    const [year, month] = value.split('-')
    return `${month}/${year}`
  }
  const date = toUtcDate(value)
  if (!date) return fallback
  const month = String(date.getUTCMonth() + 1).padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${month}/${year}`
}
